/**
 * Shared subscription + create/reply handlers for PDF/image comment pins (pdfAnnotations collection).
 */

import { useState, useCallback, useEffect, type Dispatch, type SetStateAction } from 'react';
import type { CommentMention, PdfAnnotation, User } from '../types';
import { subscribeToAnnotationsForDocument, createAnnotation, addAnnotationReply } from '../utils/pdfAnnotationService';
import { uploadPdfCommentContextPreview } from '../utils/pdfCommentContextCapture';
import { isArchitectRole, isSubcontractorRole } from '../utils/userRoles';

export interface UsePdfAnnotationsOptions {
  documentId: string;
  documentUrl: string;
  leadId: string;
  projectId?: string;
  taskId?: string;
  user: User | null | undefined;
  /** Shown immediately, and kept if the Firestore subscribe is denied. */
  initialAnnotations?: PdfAnnotation[];
  initialAnnotationId?: string;
  /** When set, only annotations on this page are stored in state (e.g. single-page image viewer). */
  annotationPageFilter?: number;
  /** Capture a JPEG data URL around the pin before saving (PDF/image viewers). */
  captureContextPreview?: (pageNumber: number, x: number, y: number) => Promise<string | undefined>;
  /** Active quiet plan-review session — stamps pins so Cloud Functions suppress per-comment notify. */
  activePlanReviewId?: string;
  decisionOnly?: boolean;
  pinAudience?: 'internal' | 'architect';
}

export interface UsePdfAnnotationsResult {
  annotations: PdfAnnotation[];
  loadingAnnotations: boolean;
  focusedAnnotationId: string | undefined;
  setFocusedAnnotationId: Dispatch<SetStateAction<string | undefined>>;
  loadAnnotations: () => void;
  handleAddAnnotation: (
    pageNumber: number,
    x: number,
    y: number,
    content: string,
    mentions?: CommentMention[]
  ) => Promise<void>;
  handleAddReply: (annotationId: string, content: string, mentions?: CommentMention[]) => Promise<void>;
}

export function usePdfAnnotations({
  documentId,
  documentUrl,
  leadId,
  projectId,
  taskId,
  user,
  initialAnnotations,
  initialAnnotationId,
  annotationPageFilter,
  captureContextPreview,
  activePlanReviewId,
  decisionOnly = false,
  pinAudience = 'internal',
}: UsePdfAnnotationsOptions): UsePdfAnnotationsResult {
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>(() => initialAnnotations ?? []);
  const [loadingAnnotations, setLoadingAnnotations] = useState(!initialAnnotations?.length);
  const [focusedAnnotationId, setFocusedAnnotationId] = useState<string | undefined>(initialAnnotationId);

  const loadAnnotations = useCallback(() => {
    /* Subscription-driven; no-op for AnnotationLayer API compatibility */
  }, []);

  useEffect(() => {
    setFocusedAnnotationId(initialAnnotationId);
  }, [initialAnnotationId]);

  useEffect(() => {
    if (!documentId) {
      setAnnotations(initialAnnotations ?? []);
      setLoadingAnnotations(false);
      return;
    }
    if (!initialAnnotations?.length) setLoadingAnnotations(true);
    const scopedProjectId =
      projectId && (isSubcontractorRole(user?.role) || isArchitectRole(user?.role) || decisionOnly)
        ? projectId
        : undefined;
    const unsub = subscribeToAnnotationsForDocument(
      documentId,
      documentUrl,
      list => {
        const live =
          annotationPageFilter != null
            ? list.filter(a => a.pageNumber === annotationPageFilter)
            : list;
        if (scopedProjectId && initialAnnotations?.length) {
          const seen = new Set(live.map(a => a.id).filter(Boolean));
          const extras = initialAnnotations.filter(a =>
            a.id && !seen.has(a.id) && (!decisionOnly || a.audience === 'architect'),
          );
          setAnnotations(
            [...live, ...extras].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
          );
        } else {
          setAnnotations(live);
        }
        setLoadingAnnotations(false);
      },
      () => {
        if (initialAnnotations?.length) setAnnotations(initialAnnotations);
        setLoadingAnnotations(false);
      },
      scopedProjectId
        ? { projectId: scopedProjectId, decisionOnly }
        : decisionOnly
          ? { decisionOnly: true }
          : undefined,
    );
    return () => unsub();
  }, [decisionOnly, documentId, documentUrl, annotationPageFilter, initialAnnotations, projectId, user?.role]);

  const handleAddAnnotation = useCallback(
    async (pageNumber: number, x: number, y: number, content: string, mentions?: CommentMention[]) => {
      if (!user?.id) return;
      try {
        let contextPreviewUrl: string | undefined;
        if (captureContextPreview) {
          try {
            const dataUrl = await captureContextPreview(pageNumber, x, y);
            if (dataUrl) {
              contextPreviewUrl = await uploadPdfCommentContextPreview(dataUrl, documentId, projectId);
            }
          } catch (previewErr) {
            console.warn('[usePdfAnnotations] context preview upload failed', previewErr);
          }
        }
        await createAnnotation({
          documentId,
          documentUrl,
          leadId,
          ...(projectId ? { projectId } : {}),
          ...(taskId ? { taskId } : {}),
          pageNumber,
          x,
          y,
          content,
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
          mentions,
          ...(contextPreviewUrl ? { contextPreviewUrl } : {}),
          ...(activePlanReviewId ? { planReviewId: activePlanReviewId } : {}),
          audience: pinAudience === 'architect' ? 'architect' : 'internal',
          createdAt: new Date(),
        });
      } catch (err) {
        console.error('Error creating annotation:', err);
      }
    },
    [documentId, documentUrl, leadId, projectId, taskId, user, captureContextPreview, activePlanReviewId, pinAudience],
  );

  const handleAddReply = useCallback(
    async (annotationId: string, content: string, mentions?: CommentMention[]) => {
      if (!user?.id) return;
      try {
        await addAnnotationReply(annotationId, {
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
          content,
          mentions,
        });
      } catch (err) {
        console.error('Error adding reply:', err);
      }
    },
    [user]
  );

  return {
    annotations,
    loadingAnnotations,
    focusedAnnotationId,
    setFocusedAnnotationId,
    loadAnnotations,
    handleAddAnnotation,
    handleAddReply,
  };
}
